import React, {useCallback, useEffect, useState} from "react";
import {Formik, useFormik} from 'formik';
import * as Yup from 'yup';
import InputMask from "react-input-mask";
import moment from "moment-timezone";
import {useLocation, useNavigate} from "react-router-dom";
import {fetchPaymentTokenDetails} from "../api/fetchPaymentService";
import {createCardTokenApi} from "../api/cardTokenService";
import {createPaymentApi} from "../api/createPaymentService";
import ThreeDSecureAuthentication from "../ThreeDSecureAuthentication";
import Loader from "../loader";
import {ConstantsContainer} from "../helper/constants";
import {fetchCsrfToken} from "../api/fetchCsrfTokenService";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faLock} from "@fortawesome/pro-thin-svg-icons";
import Timer from "./timer";

function HostedPaymentGateway() {
    const location = useLocation();
    const params = new URLSearchParams(location.search)

    const [paymentAmount, setPaymentAmount] = useState('');
    const [amountInCents, setAmountInCents] = useState('');
    const [currency, setCurrency] = useState('')
    const [merchantName, setMerchantName] = useState('');
    const [tokenExpiry, setTokenExpiry] = useState(null);

    const [token, setToken] = useState(params.get("token"))
    const [flag, setFlag] = useState(params.get("style"))
    const [accessToken, setAccessToken] = useState(params.get("accessToken"))

    const [terminalId, setTerminalId] = useState('')

    const [success, setSuccess] = useState('');
    let paymentReference = '';
    const [error, setError] = useState('');
    const [disable, setDisable] = useState(true);

    // 3D Secure Parameters
    const [threeDSecureProtocolVersion, setThreeDSecureProtocolVersion] = useState('');
    const [cardHolderAuthenticationId, setCardHolderAuthenticationId] = useState('');
    const [cardHolderAuthenticationData, setCardHolderAuthenticationData] = useState('');
    const [electronicCommerceIndicator, setElectronicCommerceIndicator] = useState('');
    const [threeDSecureDSTransitId, setThreeDSecureDSTransitId] = useState('');
    const [threeDSecureAuthenticationType, setThreeDSecureAuthenticationType] = useState('');
    const [threeDSecureVEResEnrolled, setThreeDSecureVEResEnrolled] = useState('');
    const [isAuthenticationSuccessful, setIsAuthenticationSuccessful] = useState('');
    const [paymentStage, setPaymentStage] = useState('');

    const [windowSize, setWindowSize] = useState([window.innerWidth, window.innerHeight,]);

    const navigate = useNavigate();

    useEffect(() => {
        showLoader();
        if (token === null || token.trim().length === 0) {
            return navigate("/transaction-failed", {state: {errorMessage: "Token needed"}, replace: true});
        }

        if (accessToken == null || accessToken.trim().length === 0) {
            return navigate("/transaction-failed", {state: {errorMessage: "Access Token needed"}, replace: true});
        }

        fetchPaymentTokenDetails(token, accessToken)
            .then((response) => {
                hideLoader()
                try {
                    let responseData = response.data

                    setPaymentAmount(responseData.display_amount);
                    setCurrency(responseData.currency_code);
                    setMerchantName(responseData.trading_name);
                    setTerminalId(responseData.terminal_id);
                    setAmountInCents(responseData.amount);
                    setTokenExpiry(responseData.expires);

                    setSuccess(responseData.success_callback_url);
                    setError(responseData.failure_callback_url);
                } catch (e) {
                    if (response.response.data.status !== undefined && response.response.data.status === 400) {
                        return redirectToErrorPage(response.response.data.detail)
                    } else {
                        return redirectToErrorPage(response.response.data.detail)
                    }
                }
            })
            .catch((error) => {
                handleApiErrorResponse(error)
            })

        window.addEventListener('message', function (event) {
            if (event.data.type === 'authentication-result') {
                switch (event.data.key) {
                    case "setThreeDSecureProtocolVersion":
                        setThreeDSecureProtocolVersion(event.data.value);
                        break;
                    case "setCardHolderAuthenticationId":
                        setCardHolderAuthenticationId(event.data.value);
                        break;
                    case "setCardHolderAuthenticationData":
                        setCardHolderAuthenticationData(event.data.value);
                        break;
                    case "setElectronicCommerceIndicator":
                        setElectronicCommerceIndicator(event.data.value);
                        break;
                    case "setThreeDSecureDSTransitId":
                        setThreeDSecureDSTransitId(event.data.value);
                        break;
                    case "setThreeDSecureAuthenticationType":
                        setThreeDSecureAuthenticationType(event.data.value);
                        break;
                    case "setThreeDSecureVEResEnrolled":
                        setThreeDSecureVEResEnrolled(event.data.value);
                        break;
                    case "setIsAuthenticationSuccessful":
                        setIsAuthenticationSuccessful(event.data.value);
                }
            }
        });
    }, [])

    useEffect(() => {
        const handleWindowResize = () => {
            setWindowSize([window.innerWidth, window.innerHeight]);
        };

        window.addEventListener('resize', handleWindowResize);
        if (windowSize[0] < 601 || windowSize[1] < 450) {
            setFlag('minimal')
        }

        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };

    }, []);

    useEffect(() => {
        fetchCsrfToken()
            .then((response) => {
                if (response.status === 200 || response.status === 201) {
                    let hiddenField = document.getElementById("hiddenToken");
                    hiddenField.setAttribute("value", response.data.token)
                } else {
                    handleApiErrorResponse(response.response.data)
                }
            })
            .catch((error) => {
                handleApiErrorResponse(error)
            })

    }, []);

    useEffect(() => {
        if (isAuthenticationSuccessful === true) {
            setPaymentStage(ConstantsContainer.payment_stage_authorisation);
        } else if (isAuthenticationSuccessful === false) {
            return redirectToErrorPage("Failed to authenticate customer");
        }
    }, [isAuthenticationSuccessful]);

    useEffect(() => {
        if (paymentStage === ConstantsContainer.payment_stage_authorisation && isAuthenticationSuccessful === true) {
            showLoader();
            let cardExpiry = formik.values.expiryMonth.concat('/').concat(formik.values.expiryYear.substring(2));
            createCardTokenApi(terminalId, cardExpiry, formik.values.cardNumber)
                .then((response) => {
                    try {
                        let responseData = response.data.Enquiry
                        callCreatePaymentApi(responseData.TransactionIndex, responseData.PAN, threeDSecureProtocolVersion,
                            cardHolderAuthenticationId, cardHolderAuthenticationData, electronicCommerceIndicator,
                            threeDSecureDSTransitId, formik.values.cardHolderName, cardExpiry, responseData.BIN)
                        return response
                    } catch (e) {
                        handleApiErrorResponse(e)
                    }
                })
                .catch((error) => {
                    handleApiErrorResponse(error)
                })
        }
    }, [paymentStage]);

    const handleApiErrorResponse = (error) => {
        hideLoader()
        let errorMessage = "An error occurred"
        try {
            if (error !== undefined) {
                errorMessage = error.detail;
            }
            return redirectToErrorPage(errorMessage)
        } catch (e) {
        }
    }

    const redirectToErrorPage = (errorMessage) => {
        navigate("/transaction-failed", {
            state: {
                errorRedirectionUrl: error,
                errorMessage: errorMessage,
                shopName: merchantName,
                paymentReference: paymentReference
            }, replace: true
        });
    }

    const redirectToSuccessPage = () => {
        navigate("/transaction-successful", {
            state: {
                redirectionUrl: success, paymentReference: paymentReference, shopName: merchantName
            }, replace: true
        });
    }
    const callCreateCardTokenApi = () => {
        setPaymentStage(ConstantsContainer.payment_stage_authentication);
    }

    function handlePaymentResponses(response) {
        let responseStatus = response.status !== undefined ? response.status : response.response.status;

        switch (responseStatus) {
            //Success Response Codes
            case 200:
                handleSuccess(response.data)
                break;
            case 201:
                handleSuccess(response.data)
                break;

            //Failure Response Codes
            case 400:
                redirectToErrorPage("An error occurred")
                break;
            case 401:
                redirectToErrorPage("An error occurred")
                break;
            case 404:
                redirectToErrorPage("An error occurred")
                break;
            case 406:
                redirectToErrorPage("An error occurred")
                break;
            case 410:
                redirectToErrorPage("Payment Token has already been used")
                break;
            case 422:
                redirectToErrorPage("Invalid Data")
                break;
            case 500:
                redirectToErrorPage("Internal Server Error")
                break;
            default:
                return response
        }
    }

    const handleSuccess = (response) => {
        if (response.payment_status === "Approved") {
            redirectToSuccessPage()
        } else {
            return redirectToErrorPage(response.payment_status)
        }
    }

    const callCreatePaymentApi = (
        transactionIndex, truncatedPan, threeDSecureProtocolVersion, threeDSecureCardHolderAuthenticationId,
        threeDSecureCardHolderAuthenticationData, threeDSecureElectronicCommerceIndicator, threeDSecureTransactionId,
        cardholderName, cardExpiry, cardBin
    ) => {

        let hiddenField = document.getElementById("hiddenToken");
        var hiddenToken = hiddenField.getAttribute("value")

        createPaymentApi(token, accessToken, transactionIndex, truncatedPan,
            cardExpiry, formik.values.cvc, cardBin, threeDSecureProtocolVersion, threeDSecureCardHolderAuthenticationId,
            threeDSecureCardHolderAuthenticationData, threeDSecureElectronicCommerceIndicator, threeDSecureTransactionId, hiddenToken, cardholderName)
            .then((response) => {
                paymentReference = response.data.payment_reference
                hideLoader()
                try {
                    handlePaymentResponses(response);

                } catch (e) {
                    handleApiErrorResponse(e)
                }

            })
            .catch((error) => {
                if (error.response?.data?.detail !== undefined) {
                    handleApiErrorResponse(error)
                } else {
                    hideLoader();
                    return redirectToErrorPage("Error occurred while processing payment");
                }
            })
    }

    // Takes a credit card string value and returns true on valid number
    function validateCardNumberWithLuhn(value) {
        // Accept only digits, dashes or spaces
        if (/[^0-9-\s]+/.test(value)) return false;

        // Makes sure only card with more than 12 characters are validated
        if (value.length === 0 || value.length > 0 && value.length < 15) {
            formik.values.cardNumber = ""
            return false;
        }

        // The Luhn Algorithm
        let nCheck = 0, bEven = false;
        value = value.replace(/\D/g, "");

        for (let n = value.length - 1; n >= 0; n--) {
            let cDigit = value.charAt(n), nDigit = parseInt(cDigit, 10);

            if (bEven && (nDigit *= 2) > 9) nDigit -= 9;

            nCheck += nDigit;
            bEven = !bEven;
        }

        return (nCheck % 10) === 0;
    }

    Yup.addMethod(Yup.string, "LuhnValidation", function (errorMessage) {
        return this.test('test-card-number', errorMessage, function (value) {
            const {path, createError} = this;

            return (validateCardNumberWithLuhn(value) === true ||
                createError({path, message: errorMessage})
            );
        });
    });


    const validationSchema = (minExpiryMonth, minExpiryYear) => {
        return Yup.object({
            cardHolderName: Yup.string()
                .matches(/^[A-Za-z\s]*$/, "Cardholder name must contain only alphabets")
                .required('Cardholder name is required'),

            cardNumber: Yup.string()
                .LuhnValidation("Invalid card number")
                .required("Card number is required"),

            expiryYear: Yup.number()
                .required("Card expiry is required"),

            expiryMonth: Yup.number()
                .required("Card expiry is required")
                // if selected year is current year, check if month is valid
                .when("expiryYear", selectedYear => {
                    if (parseInt(selectedYear) != NaN && parseInt(selectedYear) == minExpiryYear) {
                        return Yup.number().min(minExpiryMonth, "Your card has expired")
                    }
                }),

            cvc: Yup.string()
                .required('CVC is required')
                .min(3, 'Verify your CVC')
                .max(4, 'Verify your CVC'),
        });
    }

    const formik = useFormik({
        initialValues: {
            cardHolderName: '', cardNumber: '', expiry: '', expiryDate: '', cvc: '', expiryMonth: '', expiryYear: ''
        },
        validateOnChange: true,
        validateOnBlur: false,
        validationSchema: validationSchema(new Date().getMonth() + 1, new Date().getFullYear()),
        onSubmit: () => {
            callCreateCardTokenApi()
        }
    });

    const showLoader = () => {
        setDisable(true)
    }

    const hideLoader = () => {
        setDisable(false)
    }

    const monthOptions = [{value: "01", label: "01"}, {value: "02", label: "02"}, {
        value: "03", label: "03"
    }, {value: "04", label: "04"}, {value: "05", label: "05"}, {value: "06", label: "06"}, {
        value: "07", label: "07"
    }, {value: "08", label: "08"}, {value: "09", label: "09"}, {value: "10", label: "10"}, {
        value: "11", label: "11"
    }, {value: "12", label: "12"},]

    const yearOptions = () => {
        let currentYear = new Date().getFullYear();

        let options = [{value: currentYear.toString(), label: currentYear.toString()}]

        for (let i = 1; i < 11; i++) {
            let year = currentYear + i;
            options.push({value: year.toString(), label: year.toString()})
        }

        return options;
    }

    return (<div className="flex min-h-screen md:max-xl:flex">

        {paymentStage === 'authentication' && <ThreeDSecureAuthentication
            applicationId={ConstantsContainer.applicationid_3ds}
            amount={amountInCents}
            currency={currency}
            cvv={formik.values.cvc}
            expiryDate={formik.values.expiryMonth.concat(formik.values.expiryYear.substring(2))}
            merchantReference={token}
            pan={formik.values.cardNumber.replace(/\s/g, '')}
            returnUrl={ConstantsContainer.threeds_callback_url + '?accessToken=' + accessToken}/>}

        <div id="class-loader"
             className={"z-50 w-screen h-screen bg-white fixed inset-0 flex items-center justify-center " + (disable === true ? "visible" : "invisible")}>
            <Loader/>
        </div>

        <div className="relative w-full flex columns-1 xl:columns-2 gap-1">
            {/* Top-right Timer */}
            {tokenExpiry != null && (
                <div className="absolute top-0 right-0">
                    <Timer expiry={tokenExpiry}/>
                </div>
            )}

            {/* Left Image */}
            <div className="hidden xl:block w-1/2">
                <img src="/hostedPageCardsBackgroundImage.svg"
                     className="h-[90vh] w-auto"
                     alt=""
                />
            </div>

            {/* Right Form Section */}
            <div className="w-full xl:w-1/2 flex items-center justify-center">
                <div className="px-6 w-full text-xl xl:bg-[#F8F8F8] flex min-h-screen">
                    <div className="mx-auto mt-[2vh] w-full sm:w-3/4 md:w-1/2">
                        <form onSubmit={formik.handleSubmit}>
                            <div className="mt-2 sm:mt-[12vh] xl:mt-2 mb-4 sm:mb-10 flex justify-start tablet:justify-center">
                                <img className="h-[10vh] w-auto"
                                     src="/hellopayPoweredByNedbank.svg"
                                     alt="hellopay powered by Nedbank"
                                />
                            </div>

                            <div className="mt-[5vh]">
                                <div
                                    className="mb-2 p-2 flex rounded-md space-x-2
                                bg-very_light_purple font-semibold hellopay_text text-purple ">
                                    <div className="ml-2.5">{merchantName}</div>
                                </div>

                                <div
                                    className="grid auto-rows-auto flex rounded-md
                                p-1.5 bg-light_purple font-semibold hellopay_text text-purple">
                                    <div className="mt-1.5 mb-1.5 ml-4">Total amount to pay:</div>

                                    <div className="mb-1.5 ml-6 font-bold text-xl"> {currency} {paymentAmount}</div>

                                    <input id="hiddenToken" name="hiddenField" type="hidden"></input>
                                </div>

                                <div className="form-control
                                 mb-6 mt-4 ">
                                    <label htmlFor="cardholder_name"
                                           className="block mb-1 font-semibold hellopay_text text-base text-purple">Cardholder
                                        Name</label>

                                    <div
                                        className="flex border rounded-full border-purple rounded-lg bg-white
                                    p-1 focus-within:border-1">

                                        <input type="text" id="cardholder_name"
                                               name="cardHolderName"
                                               autoComplete="cc-name"
                                               className="uppercase ml-2 text-sm block w-full px-2.5 py-2 border-0 outline-none"
                                               maxLength={30}
                                               value={formik.values.cardHolderName}
                                               onChange={formik.handleChange}
                                               onBlur={formik.handleBlur}
                                               placeholder="Eg. J Bowman"/>
                                    </div>

                                    {formik.touched.cardHolderName && formik.errors.cardHolderName ? <div
                                        className="error h-px text-soft_red text-sm">{formik.errors.cardHolderName}</div> : null}
                                </div>

                                <div className="form-control
                                mt-4 mb-6">
                                    <label htmlFor="card_number"
                                           className="block mb-1 font-semibold hellopay_text text-base text-purple">Card
                                        Number</label>

                                    <div
                                        className="flex border rounded-full border-purple rounded-lg bg-white p-1 focus-within:border-1">

                                        <InputMask type="text"
                                                   id="card_number"
                                                   border="none"
                                                   inputmode="numeric"
                                                   name="cardNumber"
                                                   autoComplete="cc-number"
                                                   className="uppercase ml-2 rl-2 text-sm block w-full px-2.5 py-2 border-0 outline-none autofill:bg-red"
                                                   mask="9999 9999 9999 9999"
                                                   alwaysShowMask={false}
                                                   maskChar=""
                                                   placeholder="4242 xxxx xxxx xxxx"
                                                   value={formik.values.cardNumber}
                                                   onChange={formik.handleChange}
                                                   onBlur={formik.handleBlur}/>
                                    </div>

                                    {formik.touched.cardNumber && formik.errors.cardNumber ? <div
                                        className="error h-px text-soft_red text-sm">{formik.errors.cardNumber}</div> : null}
                                </div>

                                <div className="flex rounded w-full justify-between space-x-4
                                hellopay_text text-purple">

                                    <div className="w-1/3">
                                        <label htmlFor="expiry_month"
                                               className="font-semibold text-base w-80">Month</label>

                                        <div className="flex border rounded-full border-purple rounded-lg bg-white
                                    p-1 focus-within:border-1">
                                            <select
                                                id="expiry_month"
                                                name="expiryMonth"
                                                autoComplete="cc-exp-month"
                                                className={"h-9 max-h-9 max-w-full grow outline-none text-center text-sm bg-white " + (formik.values.expiryMonth === '' ? "text-gray-400" : "text-black")}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            >
                                                <option disabled selected>MM</option>
                                                {monthOptions.map(month => {
                                                    return <option value={month.value}
                                                                   className="text-black">{month.label}</option>
                                                })}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="w-1/3">
                                        <label htmlFor="expiry_year"
                                               className="font-semibold text-base w-[350px]">Year</label>

                                        <div className="flex border rounded-full border-purple rounded-lg bg-white
                                    p-1 focus-within:border-1">
                                            <select
                                                id="expiry_year"
                                                name="expiryYear"
                                                autoComplete="cc-exp-year"
                                                className={"h-9 max-h-9 max-w-full grow outline-none text-center text-sm bg-white " + (formik.values.expiryYear === '' ? "text-gray-400" : "text-black")}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            >
                                                <option disabled selected>YYYY</option>
                                                {yearOptions().map(year => {
                                                    return <option value={year.value}
                                                                   className="text-black">{year.label}</option>
                                                })}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="w-1/3">
                                        <label htmlFor="security_code"
                                               className="font-semibold text-base mb-2">CVC</label>

                                        <div
                                            className="flex border  rounded-full
                                        border-purple rounded-lg bg-white p-1 focus-within:border-1">

                                            <InputMask type="text"
                                                       inputmode="numeric"
                                                       id="security_code"
                                                       name="cvc"
                                                       mask="9999"
                                                       alwaysShowMask={false}
                                                       maskChar=""
                                                       autoComplete="cc-csc"
                                                       className="ml-2 w-16 text-sm text-black p-2 uppercase border-0 outline-none"
                                                       placeholder="CVC"
                                                       value={formik.values.cvc}
                                                       onChange={formik.handleChange}
                                                       onBlur={formik.handleBlur}/>
                                        </div>

                                        {formik.touched.cvc && formik.errors.cvc ? <div
                                            className="error h-0.5 text-soft_red text-sm">{formik.errors.cvc}</div> : null}
                                    </div>
                                </div>

                                {formik.touched.expiryYear && formik.errors.expiryYear
                                    ?
                                    <div className="error h-0.5 text-soft_red text-sm">{formik.errors?.expiryYear}</div>
                                    : formik.touched.expiryMonth && formik.errors.expiryMonth
                                        ? <div
                                            className="error h-0.5 text-soft_red text-sm">{formik.errors?.expiryMonth}</div>
                                        : null
                                }

                                <div className="flex justify-center mt-9 mb-6">
                                    <button type="submit"
                                            className={`${!formik.isValid
                                            || formik.values.cardHolderName == ''
                                            || formik.values.cardNumber == ''
                                            || formik.values.cvc == ''
                                            || formik.values.expiryMonth == ''
                                            || formik.values.expiryYear == ''
                                                ? `bg-dark_grayish_lime_green`
                                                : `bg-gradient-to-r from-purple to-very_dark_grayish_violet`}  
                                        text-sm h-10 w-80 space-x-2 px-2
                                        rounded-md text-white uppercase`}
                                            disabled={!formik.isValid
                                                || formik.values.cardHolderName == ''
                                                || formik.values.cardNumber == ''
                                                || formik.values.cvc == ''
                                                || formik.values.expiryMonth == ''
                                                || formik.values.expiryYear == ''
                                            }
                                    >
                                        <FontAwesomeIcon icon={faLock} style={{color: "#f2f2f2",}}/>
                                        <span>Pay securely</span>
                                    </button>
                                </div>

                                <div className="mb-2 w-80 flex justify-center w-auto">
                                    <img src="/card_logo_strip.svg"
                                         className="w-auto h-6"
                                         alt={"logo"}
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>);
}

export default HostedPaymentGateway;